import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CircleNotch, Copy, PaperPlaneTilt, CheckCircle, Link as LinkIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogIcon,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/lib/toast";
import { formatDateShort, formatTimeShort } from "@/lib/format";
import { throwIfNotOk, apiGet } from "@/lib/api-error";
import type { Test } from "@/types/test";
import type { TherapiesResponse } from "@/types";

interface Props {
  open: boolean;
  defaultTherapyId?: string;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

const NO_THERAPY = "__none__";

export function SendTestDialog({ open, defaultTherapyId, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation();
  const [testId, setTestId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [therapyId, setTherapyId] = useState(defaultTherapyId ?? "");
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const { data: tests } = useQuery({
    enabled: open,
    queryKey: ["therapist", "tests", "available"],
    queryFn: async () => {
      const res = await fetch("/api/tests?activeOnly=1");
      await throwIfNotOk(res, t("tests.therapist.fetchTestsError"));
      return res.json() as Promise<{ tests: Test[] }>;
    },
  });

  const { data: therapies } = useQuery({
    enabled: open,
    queryKey: ["therapist", "therapies", "for-tests"],
    queryFn: () =>
      apiGet<TherapiesResponse>(
        `/api/therapist/therapies?status=scheduled&limit=100`,
        t("tests.therapist.fetchTherapiesError"),
      ),
  });

  // Mirrors clientFirstName/LastName (trim + min 1): "   " passes `required`
  // but the server rejects it.
  const canSubmit = testId !== "" && firstName.trim() !== "" && lastName.trim() !== "";

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/test-assignments", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          testId,
          clientFirstName: firstName.trim(),
          clientLastName: lastName.trim(),
          therapyId: therapyId || undefined,
        }),
      });
      await throwIfNotOk(res, t("tests.therapist.createError"));
      return res.json() as Promise<{ token: string; url: string }>;
    },
    onSuccess: (data) => {
      setCreatedUrl(`${window.location.origin}${data.url}`);
      onCreated();
    },
  });

  const reset = () => {
    setTestId("");
    setFirstName("");
    setLastName("");
    setTherapyId(defaultTherapyId ?? "");
    setCreatedUrl(null);
  };

  const copy = async () => {
    if (!createdUrl) return;
    await navigator.clipboard.writeText(createdUrl);
    toast.success(t("tests.therapist.copied"));
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="sm:max-w-lg"
    >
      <DialogHeader onClose={handleClose} className="border-b-0 pb-0">
        <div className="flex items-center gap-3">
          <DialogIcon tone={createdUrl ? "success" : "default"}>
            {createdUrl ? (
              <CheckCircle weight="fill" className="size-5" />
            ) : (
              <PaperPlaneTilt weight="fill" className="size-5" />
            )}
          </DialogIcon>
          <div className="min-w-0">
            <DialogTitle>{t("tests.therapist.sendTitle")}</DialogTitle>
            <DialogDescription className="mt-0.5 truncate">
              {createdUrl ? t("tests.therapist.linkReady") : t("tests.therapist.sendSubtitle")}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {createdUrl ? (
        <>
          <DialogBody className="pt-5">
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2.5 rounded-xl border border-primary/20 bg-primary/[0.04] p-3">
                <LinkIcon weight="bold" className="mt-0.5 size-4 shrink-0 text-primary" />
                <code className="flex-1 break-all font-mono text-xs leading-relaxed text-foreground">
                  {createdUrl}
                </code>
              </div>
              <Button type="button" variant="outline" onClick={copy} className="w-full">
                <Copy />
                {t("tests.therapist.copyLink")}
              </Button>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={reset}>
              {t("tests.therapist.sendAnother")}
            </Button>
            <Button type="button" onClick={() => onOpenChange(false)}>
              {t("common.done")}
            </Button>
          </DialogFooter>
        </>
      ) : (
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
        >
          <DialogBody className="pt-5">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="send-test" className="text-sm font-medium text-foreground">
                  {t("tests.therapist.fieldTest")}
                </Label>
                <Select value={testId} onValueChange={setTestId} required>
                  <SelectTrigger id="send-test" className="w-full">
                    <SelectValue placeholder={t("tests.therapist.pickTest")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tests?.tests.map((te) => (
                      <SelectItem key={te.id} value={te.id}>
                        {te.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium text-foreground">
                  {t("tests.therapist.fieldClient")}
                </Label>
                <div className="grid grid-cols-2 gap-2.5">
                  <Input
                    required
                    value={firstName}
                    maxLength={100}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t("tests.therapist.firstName")}
                  />
                  <Input
                    required
                    value={lastName}
                    maxLength={100}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t("tests.therapist.lastName")}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="send-therapy" className="text-sm font-medium text-foreground">
                  {t("tests.therapist.fieldTherapy")}{" "}
                  <span className="font-normal text-muted-foreground">({t("common.optional")})</span>
                </Label>
                <Select
                  value={therapyId === "" ? NO_THERAPY : therapyId}
                  onValueChange={(v) => setTherapyId(v === NO_THERAPY ? "" : v)}
                >
                  <SelectTrigger id="send-therapy" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_THERAPY}>{t("tests.therapist.therapyNone")}</SelectItem>
                    {therapies?.data.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="py-2">
                        <span className="flex flex-col gap-0.5">
                          <span className="font-medium text-foreground">{s.clientName}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateShort(s.scheduledAt)} · {formatTimeShort(s.scheduledAt)} · {s.serviceName}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {mutation.isPending && <CircleNotch className="animate-spin" />}
              {t("tests.therapist.generate")}
            </Button>
          </DialogFooter>
        </form>
      )}
    </Dialog>
  );
}
